// Flux owns its sidebar state. This adapter observes that state and supplies
// mobile dialog semantics through the same toggle event as Flux's own controls.
const selector = '[data-member-drawer][data-flux-sidebar]';
const cleanups = new Set();
const bound = new WeakSet();
const focusable = 'a[href], button, input, select, textarea, summary, [tabindex]';
const visible = node => !node.closest('[inert]') && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden';

function bind(drawer) {
    if (bound.has(drawer)) return;
    bound.add(drawer);
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const triggers = [...document.querySelectorAll('.ff-member-mobile-header [data-flux-sidebar-toggle]')];
    const original = new Map(['role', 'aria-modal', 'tabindex', 'id'].map(name => [name, drawer.getAttribute(name)]));
    drawer.id ||= 'member-navigation-drawer';
    const closeButton = drawer.querySelector('[data-member-drawer-close]');
    const originalCloseLabel = closeButton?.getAttribute('aria-label');
    const tooltips = [...drawer.querySelectorAll('ui-tooltip')].map(node => [node, node.hasAttribute('disabled')]);
    let opener;
    let active = false;
    let background = [];
    let overflow;
    const mobile = () => drawer.hasAttribute('data-flux-sidebar-on-mobile');
    const open = () => mobile() && !drawer.hasAttribute('data-flux-sidebar-collapsed-mobile');
    const items = () => [...drawer.querySelectorAll(focusable)].filter(node => !node.disabled && node.tabIndex >= 0 && visible(node));
    const focusFirst = () => (closeButton && visible(closeButton) ? closeButton : items()[0] || drawer).focus();
    const dismiss = () => {
        if (open()) drawer.dispatchEvent(new CustomEvent('flux-sidebar-toggle', { bubbles: true }));
    };
    function release(restoreFocus) {
        for (const [node, inert] of background) node.inert = inert;
        background = [];
        document.body.style.overflow = overflow;
        drawer.removeAttribute('aria-modal');
        drawer.removeAttribute('role');
        active = false;
        if (restoreFocus && opener?.isConnected && visible(opener)) opener.focus({ preventScroll: true });
    }
    function sync() {
        const expanded = open();
        // Expanded mobile links already expose their labels. Flux's desktop
        // tooltip focus-return can re-enter showPopover during sequential Tab.
        for (const [tooltip, disabled] of tooltips) tooltip.toggleAttribute('disabled', mobile() || disabled);
        drawer.inert = mobile() && !expanded;
        for (const trigger of triggers) {
            trigger.setAttribute('aria-controls', drawer.id);
            trigger.setAttribute('aria-expanded', String(expanded));
            trigger.setAttribute('aria-label', 'Open member navigation');
        }
        if (expanded && !active) {
            active = true;
            opener = triggers.find(node => node === document.activeElement) || triggers.find(visible);
            drawer.setAttribute('role', 'dialog');
            drawer.setAttribute('aria-modal', 'true');
            drawer.setAttribute('tabindex', '-1');
            if (closeButton) closeButton.setAttribute('aria-label', 'Close member navigation');
            // The festival drawer is nested: exclude siblings along its ancestor
            // path, retaining Flux's backdrop as its existing pointer dismissal.
            for (let branch = drawer; branch.parentElement; branch = branch.parentElement) {
                for (const sibling of branch.parentElement.children) {
                    if (sibling === branch || sibling.matches('[data-flux-sidebar-backdrop], script, style, link')) continue;
                    background.push([sibling, sibling.inert]);
                    sibling.inert = true;
                }
                if (branch.parentElement === document.body) break;
            }
            overflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            focusFirst();
        } else if (!expanded && active) release(mobile());
    }
    document.addEventListener('keydown', event => {
        if (!active) return;
        if (event.key === 'Escape') {
            // Nested menus/dialogs get the first Escape, including Flux popovers.
            const nested = drawer.querySelector(':popover-open:not([data-flux-tooltip-content]), dialog[open], details[open]');
            queueMicrotask(() => {
                if (!nested && !event.defaultPrevented && open()) {
                    event.preventDefault();
                    dismiss();
                }
            });
        } else if (event.key === 'Tab' && !event.defaultPrevented) {
            const list = items();
            const first = list[0]; const last = list.at(-1);
            if (!first) { event.preventDefault(); drawer.focus(); }
            else if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
                event.preventDefault(); last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
                event.preventDefault(); first.focus();
            }
        }
    }, options);
    document.addEventListener('focusin', event => {
        if (active && !drawer.contains(event.target)) focusFirst();
    }, options);
    const observer = new MutationObserver(sync);
    observer.observe(drawer, { attributes: true, attributeFilter: ['data-flux-sidebar-on-mobile', 'data-flux-sidebar-collapsed-mobile'] });
    const cleanup = () => {
        observer.disconnect(); controller.abort();
        if (active) release(false);
        drawer.inert = false;
        for (const [tooltip, disabled] of tooltips) tooltip.toggleAttribute('disabled', disabled);
        for (const [name, value] of original) value === null ? drawer.removeAttribute(name) : drawer.setAttribute(name, value);
        if (closeButton) originalCloseLabel === null ? closeButton.removeAttribute('aria-label') : closeButton.setAttribute('aria-label', originalCloseLabel);
        bound.delete(drawer); cleanups.delete(cleanup);
    };
    cleanups.add(cleanup);
    sync();
}
function initialize() {
    customElements.whenDefined('ui-sidebar').then(() => document.querySelectorAll(selector).forEach(bind));
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
else initialize();
document.addEventListener('livewire:navigating', () => {
    for (const cleanup of [...cleanups]) cleanup();
});
document.addEventListener('livewire:navigated', initialize);
